import { AlertCircle, X } from "lucide-react";
import { getBankingContractPdf } from "../../../../Rest_api/RestApi";

export type PdfPopupState = { type: "info" | "error"; message: string };

/**
 * Runs the contract-PDF download flow: fetches a fresh signed URL, saves the file
 * as a blob (forcing a save dialog), and falls back to opening the signed URL in a
 * new tab if the cross-origin S3 blob fetch fails on CORS.
 *
 * Returns a PdfPopupState to display (info when no PDF exists, error on failure),
 * or null when the download succeeded and nothing needs to be shown.
 */
export async function downloadBankingContractPdf(
  actualId: string,
): Promise<PdfPopupState | null> {
  try {
    // Always fetch a fresh signed URL — the cached pdf_download_url expires (~1 hour).
    const res = await getBankingContractPdf(actualId);
    if (!res?.download_url) {
      return {
        type: "info",
        message:
          "No PDF is available for this contract — it was added manually.",
      };
    }
    const filename = res.filename || "contract.pdf";
    try {
      // Force a save dialog by downloading the file as a blob.
      const fileRes = await fetch(res.download_url);
      if (!fileRes.ok) throw new Error(`HTTP ${fileRes.status}`);
      const blob = await fileRes.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      // Blob fetch can fail on cross-origin S3 CORS. Fall back to an anchor
      // download pointed straight at the signed URL — this stays in the current
      // tab (no _blank popup); the browser downloads it, or navigates to the PDF
      // in place if it can't inline-download a cross-origin file.
      const a = document.createElement("a");
      a.href = res.download_url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    return null;
  } catch (err: any) {
    return {
      type: "error",
      message:
        typeof err === "string"
          ? err
          : err?.message || "Failed to download contract PDF.",
    };
  }
}

/**
 * Info/error modal shown after a PDF download attempt. Renders nothing when
 * `popup` is null. Markup shared by ContractCard and DocumentsTab.
 */
export function PdfDownloadPopup({
  popup,
  onClose,
}: {
  popup: PdfPopupState | null;
  onClose: () => void;
}) {
  if (!popup) return null;
  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center px-4 bg-black/20 backdrop-blur-[2px]">
      <div className="bg-white rounded-2xl shadow-[0_20px_60px_-10px_rgba(0,0,0,0.18)] w-full max-w-[380px] overflow-hidden">
        <div
          className={`w-full ${
            popup.type === "error"
              ? "bg-gradient-to-r from-[#E05252] to-[#C0392B]"
              : "bg-gradient-to-r from-[#F1935C] to-[#E07B43]"
          }`}
          style={{ height: 4 }}
        />
        <div className="flex flex-col items-center px-6 pt-7 pb-2 gap-3 relative">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-lg text-[#B0AEC0] hover:text-[#6F6D7C] hover:bg-[#F5F6F8] cursor-pointer bg-transparent border-none"
          >
            <X size={15} />
          </button>
          <div
            className={`w-14 h-14 rounded-full flex items-center justify-center ${
              popup.type === "error" ? "bg-[#FEF2F2]" : "bg-[#FFF7F0]"
            }`}
          >
            <AlertCircle
              size={28}
              className={
                popup.type === "error" ? "text-[#C8311C]" : "text-[#E07B43]"
              }
            />
          </div>
          <div className="flex flex-col items-center gap-1 text-center">
            <span
              className={`text-[15px] font-bold ${
                popup.type === "error" ? "text-[#C8311C]" : "text-[#454450]"
              }`}
            >
              {popup.type === "error" ? "Download failed" : "No PDF available"}
            </span>
          </div>
        </div>
        <div
          className={`mx-6 mb-0 px-3 py-3 rounded-xl border text-center ${
            popup.type === "error"
              ? "bg-[#FEF2F2] border-[#FECACA]"
              : "bg-[#FFF7F0] border-[#F6D9C2]"
          }`}
        >
          <p
            className={`text-[12px] leading-relaxed break-words ${
              popup.type === "error" ? "text-[#7B3A35]" : "text-[#7A5638]"
            }`}
          >
            {popup.message}
          </p>
        </div>
        <div className="px-6 py-4 flex justify-center">
          <button
            type="button"
            onClick={onClose}
            className="px-8 py-2 rounded-lg bg-[#F1935C] text-white text-[12px] font-semibold hover:bg-[#E07B43] cursor-pointer border-none transition-colors"
          >
            OK, got it
          </button>
        </div>
      </div>
    </div>
  );
}

export default PdfDownloadPopup;
